import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Filter, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  reportsService,
  type CashRegisterFilterOption,
  type CashMovementReportFilters,
  type CashMovementReportResult,
} from '../../../services/reports.service'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Button } from '../../ui/button'
import { GlobalPaginator } from '../../ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { TabsContent } from '../../ui/tabs'

const DEFAULT_FILTERS: CashMovementReportFilters = {
  direction: 'all',
  movement: 'all',
}

const REPORT_TABLE_HEAD_CLASS =
  'h-auto px-6 py-3 text-xs font-medium uppercase tracking-wider text-table-header-foreground'
const REPORT_TABLE_CELL_CLASS = 'px-6 py-4 text-table-foreground'

interface CashMovementRow {
  date: string
  movement: string
  folio: string | null
  related_document: string | null
  user: string
  cash_register_branch: string
  payment_method: string
  comment: string | null
  entry: number | null
  exit: number | null
}

export interface AppliedCashMovementReportContext {
  filters: CashMovementReportFilters
}

interface MovimientosTabProps {
  onAppliedContextChange: (context: AppliedCashMovementReportContext) => void
}

export function MovimientosTab({
  onAppliedContextChange,
}: MovimientosTabProps) {
  const [draftFilters, setDraftFilters] =
    useState<CashMovementReportFilters>(DEFAULT_FILTERS)
  const [appliedFilters, setAppliedFilters] =
    useState<CashMovementReportFilters>(DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  const queryFilters = useMemo(
    () => ({ ...appliedFilters, page }),
    [appliedFilters, page],
  )
  const optionsQuery = useQuery({
    queryKey: ['reports', 'cash-movements', 'filter-options'],
    queryFn: () => reportsService.getCashMovementFilterOptions(),
    staleTime: 5 * 60 * 1000,
  })
  const reportQuery = useQuery({
    queryKey: ['reports', 'cash-movements', queryFilters],
    queryFn: () =>
      reportsService.getCashMovementsReport<CashMovementRow>(queryFilters),
    placeholderData: (previous) => previous,
  })
  const registerOptions = useMemo(() => {
    if (!draftFilters.sucursal_id)
      return optionsQuery.data?.cash_registers ?? []

    return (optionsQuery.data?.cash_registers ?? []).filter(
      (register) => register.sucursal_id === draftFilters.sucursal_id,
    )
  }, [draftFilters.sucursal_id, optionsQuery.data?.cash_registers])

  useEffect(() => {
    onAppliedContextChange({ filters: appliedFilters })
  }, [appliedFilters, onAppliedContextChange])

  const updateFilters = (nextFilters: CashMovementReportFilters) => {
    setDraftFilters(nextFilters)
  }

  const clearFilters = () => {
    setDraftFilters(DEFAULT_FILTERS)
    setAppliedFilters(DEFAULT_FILTERS)
    setPage(1)
  }

  return (
    <TabsContent value="movimientos" className="space-y-5">
      <GlobalFilterBar
        show={['dates']}
        values={{
          startDate: String(draftFilters.date_from ?? ''),
          endDate: String(draftFilters.date_to ?? ''),
        }}
        onChange={(changes) => {
          const nextFilters = { ...draftFilters }
          if (changes.startDate !== undefined) {
            nextFilters.date_from = changes.startDate || undefined
          }
          if (changes.endDate !== undefined) {
            nextFilters.date_to = changes.endDate || undefined
          }
          updateFilters(nextFilters)
        }}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters(draftFilters)}
      >
        <OptionsSelect
          label="Sucursal"
          value={draftFilters.sucursal_id}
          options={optionsQuery.data?.sucursals}
          onChange={(value) =>
            updateFilters({
              ...draftFilters,
              sucursal_id: value === 'all' ? undefined : Number(value),
              cash_register_id: undefined,
            })
          }
        />
        <OptionsSelect
          label="Caja"
          value={draftFilters.cash_register_id}
          options={registerOptions}
          optionLabel={registerOptionLabel}
          onChange={(value) =>
            updateFilters({
              ...draftFilters,
              cash_register_id: value === 'all' ? undefined : Number(value),
            })
          }
        />
        <OptionsSelect
          label="Tipo"
          value={draftFilters.direction ?? 'all'}
          options={optionsQuery.data?.directions}
          allowAll={false}
          onChange={(value) =>
            updateFilters({
              ...draftFilters,
              direction: value as CashMovementReportFilters['direction'],
            })
          }
        />
        <OptionsSelect
          label="Movimiento"
          value={draftFilters.movement ?? 'all'}
          options={optionsQuery.data?.movements}
          allowAll={false}
          onChange={(value) =>
            updateFilters({
              ...draftFilters,
              movement: value as CashMovementReportFilters['movement'],
            })
          }
        />
        <OptionsSelect
          label="Forma de pago"
          value={draftFilters.payment_method_id}
          options={optionsQuery.data?.payment_methods}
          onChange={(value) =>
            updateFilters({
              ...draftFilters,
              payment_method_id: value === 'all' ? undefined : Number(value),
            })
          }
        />
        {optionsQuery.isLoading && (
          <span className="flex items-center gap-1 self-center text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Catálogos
          </span>
        )}
        <div className="flex items-end">
          <Button
            type="button"
            onClick={() => {
              setAppliedFilters(draftFilters)
              setPage(1)
            }}
          >
            <Filter className="mr-2 h-4 w-4" /> Aplicar filtros
          </Button>
        </div>
      </GlobalFilterBar>

      <CashMovementResults
        result={reportQuery.data}
        isLoading={reportQuery.isLoading}
        isFetching={reportQuery.isFetching}
        error={reportQuery.error}
        onPageChange={setPage}
      />
    </TabsContent>
  )
}

function CashMovementResults({
  result,
  isLoading,
  isFetching,
  error,
  onPageChange,
}: {
  result?: CashMovementReportResult<CashMovementRow>
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  onPageChange: (page: number) => void
}) {
  if (isLoading) {
    return (
      <TableSkeleton
        headers={[
          'Fecha',
          'Movimiento',
          'Folio',
          'Documento relacionado',
          'Usuario',
          'Caja de cobro / Sucursal',
          'Forma de pago',
          'Comentario',
          'Entrada',
          'Salida',
        ]}
        columns={[
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
        ]}
        rows={10}
      />
    )
  }
  if (error) {
    return (
      <ReportState
        icon={<AlertCircle className="h-6 w-6 text-red-500" />}
        message="No fue posible cargar el reporte. Intenta nuevamente."
      />
    )
  }
  if (!result || result.rows.length === 0) {
    return (
      <ReportState message="No hay registros para los filtros aplicados." />
    )
  }

  const unavailable = Number(result.totals.unavailable_amount_count ?? 0)

  return (
    <div className="border-border bg-table-bg text-table-foreground overflow-hidden rounded-lg border shadow-sm">
      <div className="border-border flex items-center justify-between border-b px-6 py-4">
        <div>
          <h3 className="text-table-foreground font-semibold">Resultados</h3>
          <p className="text-muted-foreground text-xs">
            {result.pagination.total} registros filtrados
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            Movimiento neto: {formatMoney(result.totals.net_movement)}
          </p>
          {unavailable > 0 && (
            <p className="mt-1 text-xs text-amber-700">
              {unavailable} movimientos no pudieron valorarse.
            </p>
          )}
        </div>
        {isFetching && (
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        )}
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-max">
          <TableHeader className="border-border bg-table-header-bg text-table-header-foreground border-b">
            <TableRow className="hover:bg-transparent">
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>Fecha</TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>
                Movimiento
              </TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>Folio</TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>
                Documento relacionado
              </TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>Usuario</TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>
                Caja de cobro / Sucursal
              </TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>
                Forma de pago
              </TableHead>
              <TableHead className={REPORT_TABLE_HEAD_CLASS}>
                Comentario
              </TableHead>
              <TableHead className={`${REPORT_TABLE_HEAD_CLASS} text-right`}>
                Entrada
              </TableHead>
              <TableHead className={`${REPORT_TABLE_HEAD_CLASS} text-right`}>
                Salida
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="[&_tr]:border-border">
            {result.rows.map((row, index) => (
              <TableRow
                key={`${result.pagination.current_page}-${row.date}-${row.movement}-${row.folio ?? 'sin-folio'}-${index}`}
                className="hover:bg-table-row-hover"
              >
                <TableCell
                  className={`${REPORT_TABLE_CELL_CLASS} whitespace-nowrap`}
                >
                  {row.date}
                </TableCell>
                <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-medium`}>
                  {row.movement}
                </TableCell>
                <TableCell className={REPORT_TABLE_CELL_CLASS}>
                  {row.folio ?? '—'}
                </TableCell>
                <TableCell className={REPORT_TABLE_CELL_CLASS}>
                  {row.related_document ?? '—'}
                </TableCell>
                <TableCell className={REPORT_TABLE_CELL_CLASS}>
                  {row.user}
                </TableCell>
                <TableCell className={REPORT_TABLE_CELL_CLASS}>
                  {row.cash_register_branch}
                </TableCell>
                <TableCell className={REPORT_TABLE_CELL_CLASS}>
                  {row.payment_method}
                </TableCell>
                <TableCell
                  className={`${REPORT_TABLE_CELL_CLASS} max-w-sm whitespace-normal break-words`}
                >
                  {row.comment ?? '—'}
                </TableCell>
                <TableCell
                  className={`${REPORT_TABLE_CELL_CLASS} text-right font-medium text-emerald-700 tabular-nums`}
                >
                  {row.entry === null ? '—' : formatMoney(row.entry)}
                </TableCell>
                <TableCell
                  className={`${REPORT_TABLE_CELL_CLASS} text-right font-medium text-red-700 tabular-nums`}
                >
                  {row.exit === null
                    ? row.entry === null
                      ? 'No disponible'
                      : '—'
                    : formatMoney(row.exit)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter className="border-border bg-table-header-bg text-table-foreground border-t font-semibold">
            <TableRow className="hover:bg-transparent">
              <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
                TOTAL GENERAL
              </TableCell>
              <TableCell className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}>
                Registros: {result.totals.record_count ?? 0}
              </TableCell>
              <TableCell
                colSpan={6}
                className={`${REPORT_TABLE_CELL_CLASS} font-semibold`}
              >
                {unavailable > 0
                  ? `Importes no disponibles: ${unavailable}`
                  : ''}
              </TableCell>
              <TableCell
                className={`${REPORT_TABLE_CELL_CLASS} text-right font-semibold tabular-nums`}
              >
                {formatMoney(result.totals.entry)}
              </TableCell>
              <TableCell
                className={`${REPORT_TABLE_CELL_CLASS} text-right font-semibold tabular-nums`}
              >
                {formatMoney(result.totals.exit)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <GlobalPaginator
        totalItems={result.pagination.total}
        itemsPerPage={result.pagination.per_page}
        currentPage={result.pagination.current_page}
        from={result.pagination.from ?? 0}
        to={result.pagination.to ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function hasActiveFilters(filters: CashMovementReportFilters) {
  const keys = new Set([
    ...Object.keys(DEFAULT_FILTERS),
    ...Object.keys(filters),
  ])

  return Array.from(keys).some((key) => filters[key] !== DEFAULT_FILTERS[key])
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

function OptionsSelect({
  label,
  value,
  options = [],
  allowAll = true,
  optionLabel,
  onChange,
}: {
  label: string
  value: unknown
  options?: CashRegisterFilterOption[]
  allowAll?: boolean
  optionLabel?: (option: CashRegisterFilterOption) => string
  onChange: (value: string) => void
}) {
  const selected = value === undefined || value === null ? 'all' : String(value)

  return (
    <Field label={label}>
      <Select value={selected} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder="Todos" />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="all">Todos</SelectItem>}
          {options.map((option) => {
            const optionValue = String(option.id ?? option.value)
            return (
              <SelectItem key={optionValue} value={optionValue}>
                {optionLabel?.(option) ??
                  option.name ??
                  option.label ??
                  optionValue}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </Field>
  )
}

function registerOptionLabel(option: CashRegisterFilterOption) {
  const name = option.code ? `${option.name} (${option.code})` : option.name
  const state = option.is_active === false ? ' · Inactiva' : ''

  return `${name ?? 'Caja'} / ${option.branch ?? 'Sucursal'}${state}`
}

function ReportState({ icon, message }: { icon?: ReactNode; message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {icon}
      <span>{message}</span>
    </div>
  )
}

function formatMoney(value: string | number | null | undefined) {
  if (value === null || value === undefined) return 'No disponible'

  return Number(value).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
  })
}
